import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Player, LeagueInput } from '../generated-api';
import { DefaultService } from '../generated-api';
import PlayerSelectionGrid from '../components/PlayerSelectionGrid';
import { useLeagues, useData } from '../contexts/DataContext';
import PageHeader from '../components/common/PageHeader';
import LeagueInfoFields from '../components/LeagueInfoFields';
import { trackAction } from '../services/telemetry';

const NewLeaguePage: React.FC = () => {
  const { invalidate: invalidateLeagues } = useLeagues();
  const { leagues } = useData();
  const [leagueName, setLeagueName] = useState('');
  const [leagueIcon, setLeagueIcon] = useState('');
  const [leagueDescription, setLeagueDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    fetchPlayers();
  }, []);

  const fetchPlayers = async () => {
    try {
      setPlayersLoading(true);
      const response = await DefaultService.getPlayersPlayersGet();
      setAllPlayers(response || []);
    } catch (err) {
      setError('Failed to fetch players');
      console.error('Error fetching players:', err);
    } finally {
      setPlayersLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!leagueName.trim()) {
      setError('League name is required');
      return;
    }

    if (selectedPlayers.size === 0) {
      setError('Please select at least one player for the league');
      return;
    }

    try {
      setLoading(true);

      const leagueData: LeagueInput = {
        name: leagueName.trim(),
        icon: leagueIcon.trim() || undefined,
        description: leagueDescription.trim() || undefined,
        playerIds: Array.from(selectedPlayers),
        isPublic: isPublic
      };

      const response = await DefaultService.createLeagueLeaguesPost({ requestBody: leagueData });
      trackAction('league_created');

      invalidateLeagues();

      if (response) {
        navigate(`/leagues/${response.id}`);
      } else {
        navigate('/leagues');
      }
    } catch (err) {
      console.error('Error creating league:', err);
      setError('Failed to create league. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlayerIds = Array.from(selectedPlayers);

  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  return (
    <div>
      {/* Header */}
      <PageHeader className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/leagues"
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create New League</h1>
            <p className="mt-2 text-gray-600">Set up a new league for a group of players</p>
          </div>
        </div>
      </PageHeader>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
                <div className="ml-auto pl-3">
                  <button
                    type="button"
                    onClick={() => setError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Basic League Information */}
          <div className="bg-white shadow-md rounded-xl overflow-hidden">
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-100">
              <h2 className="text-lg font-semibold text-gray-900">League Information</h2>
            </div>
            <div className="p-6">
              <LeagueInfoFields
                name={leagueName}
                onNameChange={setLeagueName}
                icon={leagueIcon}
                onIconChange={setLeagueIcon}
                description={leagueDescription}
                onDescriptionChange={setLeagueDescription}
                isPublic={isPublic}
                onIsPublicChange={setIsPublic}
              />
            </div>
          </div>

          {/* Player Selection */}
          {playersLoading ? (
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-100">
                <h2 className="text-lg font-semibold text-gray-900">Select Players</h2>
              </div>
              <div className="p-6">
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading players...</p>
                </div>
              </div>
            </div>
          ) : allPlayers.length === 0 ? (
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-100">
                <h2 className="text-lg font-semibold text-gray-900">Select Players</h2>
              </div>
              <div className="p-6">
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No players found. You need to create some players first.</p>
                  <Link
                    to="/players"
                    className="text-primary-700 hover:text-primary-800 font-medium"
                  >
                    Go to Players
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <PlayerSelectionGrid
              players={allPlayers}
              selectedPlayerIds={selectedPlayerIds}
              rankedPlayers={[]}
              teams={[]}
              onTogglePlayerSelection={togglePlayerSelection}
              onCreateTeam={() => {}}
              onAddPlayerToTeam={() => {}}
              onRemovePlayerFromTeam={() => {}}
              getPlayerTeam={() => undefined}
              getTeamColor={() => undefined}
              leagues={leagues}
            />
          )}

          {/* Preview */}
          {leagueName && selectedPlayers.size > 0 && (
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-100">
                <h3 className="text-lg font-semibold text-gray-900">League Preview</h3>
              </div>
              <div className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="text-2xl">{leagueIcon || '🏆'}</div>
                  <div>
                    <div className="font-medium text-gray-900">{leagueName}</div>
                    {leagueDescription && (
                      <div className="text-sm text-gray-600">{leagueDescription}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      {selectedPlayers.size} player{selectedPlayers.size !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <Link
              to="/leagues"
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading || !leagueName.trim() || selectedPlayers.size === 0}
              className="px-6 py-2 bg-primary-700 text-white rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create League'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewLeaguePage;
